import React from "react";
import { Box } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";

// Simplified history components — no API/search calls, all manual input
import MedicalHistorySimplified from "../doctor-console/HistoriesData/MedicalHistorySimplified";
import FamilyHistorySimplified from "../doctor-console/HistoriesData/FamilyHistorySimplified";
import AllergyIntoleranceSimplified from "../doctor-console/HistoriesData/AllergyIntoleranceSimplified";
import AddictionHistory from "../doctor-console/HistoriesData/AddictionHistory";
import DrugHistorySimplified from "../doctor-console/HistoriesData/DrugHistorySimplified";
import SurgicalHistorySimplified from "../doctor-console/HistoriesData/SurgicalHistorySimplified";
import AdultVaccinationHistorySimplified from "../doctor-console/HistoriesData/AdultVaccinationHistorySimplified";
import MenstrualHistory from "../doctor-console/HistoriesData/MenstrualHistory";

// Custom MUI theme exposing palette entries like `defaultColor`, `grey.main`,
// `apple`, etc. that the doctor-console history components rely on. Without
// this provider, MUI's default theme is used and palette lookups such as
// `theme.palette.defaultColor.main` or Button `color="defaultColor"` throw
// "Cannot read properties of undefined (reading 'main' / 'dark')".
import { theme as doctorConsoleTheme } from "../doctor-console/StyledComponents";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

import { CaseSheetSection } from "./CaseSheetModalComponents";

const HistoryTab = ({
  medicalBackground,
  updateMedicalBackground,
  gender,
  fieldLayouts = {}
}) => {


  if (!medicalBackground) return null;

  const dateForAppointment = new Date();
  const noop = () => {};

  return (
    <ThemeProvider theme={doctorConsoleTheme}>
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <CaseSheetSection title="Medical History" defaultExpanded>
        <MedicalHistorySimplified
          medicalHistoryData={medicalBackground["medical_history"]}
          setMedicalHistoryData={(d) => updateMedicalBackground("medical_history", d)}
          setActiveHistorySection={noop}
          isAiEnabled={false}
          fieldLayout={fieldLayouts["History of Past Illness"]}
        />
      </CaseSheetSection>

      <CaseSheetSection title="History of Present Illness" defaultExpanded>
        <MedicalHistorySimplified
          medicalHistoryData={medicalBackground["History of Present Illness"]}
          setMedicalHistoryData={(d) => updateMedicalBackground("History of Present Illness", d)}
          setActiveHistorySection={noop}
          isAiEnabled={false}
          fieldLayout={fieldLayouts["History of Present Illness"]}
        />
      </CaseSheetSection>

      <CaseSheetSection title="Family History" defaultExpanded>
        <FamilyHistorySimplified
          familyHistoryData={medicalBackground["Family History"]}
          setFamilyHistoryData={(d) => updateMedicalBackground("Family History", d)}
          setActiveHistorySection={noop}
          isAiEnabled={false}
          fieldLayout={fieldLayouts["Family History"]}
        />
      </CaseSheetSection>

      <CaseSheetSection title="Drug History" defaultExpanded>
        <DrugHistorySimplified
          drugHistoryData={medicalBackground["Drug History"]}
          setDrugHistoryData={(d) => updateMedicalBackground("Drug History", d)}
          setActiveHistorySection={noop}
          isAiEnabled={false}
          fieldLayout={fieldLayouts["Drug History"]}
        />
      </CaseSheetSection>

      <CaseSheetSection title="Allergies" defaultExpanded>
        <AllergyIntoleranceSimplified
          allergyHistoryData={medicalBackground["Allergies"]}
          setAllergyHistoryData={(d) => updateMedicalBackground("Allergies", d)}
          setActiveHistorySection={noop}
          isAiEnabled={false}
          fieldLayout={fieldLayouts["Allergies"]}
        />
      </CaseSheetSection>

      <CaseSheetSection title="Addiction History" defaultExpanded>
        <AddictionHistory
          addictionHistoryData={medicalBackground["Addiction History"]}
          setAddictionHistoryData={(d) => updateMedicalBackground("Addiction History", d)}
          dateForAppointment={dateForAppointment}
          isAiEnabled={false}
          fieldLayout={fieldLayouts["Addiction History"]}
        />
      </CaseSheetSection>

      <CaseSheetSection title="Surgeries" defaultExpanded>
        <SurgicalHistorySimplified
          surgicalHistoryData={medicalBackground["Surgeries"]}
          setSurgicalHistoryData={(d) => updateMedicalBackground("Surgeries", d)}
          setActiveHistorySection={noop}
          isAiEnabled={false}
          fieldLayout={fieldLayouts["Surgeries"]}
        />
      </CaseSheetSection>

      <CaseSheetSection title="Adult Vaccination" defaultExpanded>
        <AdultVaccinationHistorySimplified
          adultVaccinationHistoryData={medicalBackground["Adult Vaccination"]}
          setAdultVaccinationHistoryData={(d) => updateMedicalBackground("Adult Vaccination", d)}
          isAiEnabled={false}
          fieldLayout={fieldLayouts["Adult Vaccination"]}
        />
      </CaseSheetSection>

      {/* Menstrual History - Only shown for Female patients */}
      {(gender === "Female" || gender === "F") && (
        <CaseSheetSection title="Menstrual History" defaultExpanded>
          <MenstrualHistory
            menstrualHistoryData={medicalBackground["Menstrual History"]}
            setMenstrualHistoryData={(d) => updateMedicalBackground("Menstrual History", d)}
            dateForAppointment={dateForAppointment}
            isAiEnabled={false}
            fieldLayout={fieldLayouts["Menstrual History"]}
          />
        </CaseSheetSection>
      )}

      </LocalizationProvider>
    </Box>
    </ThemeProvider>
  );
};

export default HistoryTab;
